import { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, DEV_USERS } from '../hooks/useAuth';
import { Avatar, initials } from './ui';
import { useQuery } from '../hooks/useQuery';
import { useTheme, type ThemeMode } from '../hooks/useTheme';
import { useSidebar } from '../hooks/useSidebar';
import { api } from '../lib/api';

const SIDEBAR_WIDTH = 196;
const SIDEBAR_WIDTH_COLLAPSED = 56;

const THEME_ICONS: Record<ThemeMode, ReactNode> = {
  system: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="2.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <line x1="5" y1="14" x2="11" y2="14" stroke="currentColor" strokeWidth="1.2" />
      <line x1="8" y1="11.5" x2="8" y2="14" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  light: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.2" />
      <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <line x1="8" y1="1.5" x2="8" y2="3" />
        <line x1="8" y1="13" x2="8" y2="14.5" />
        <line x1="1.5" y1="8" x2="3" y2="8" />
        <line x1="13" y1="8" x2="14.5" y2="8" />
        <line x1="3.4" y1="3.4" x2="4.5" y2="4.5" />
        <line x1="11.5" y1="11.5" x2="12.6" y2="12.6" />
        <line x1="3.4" y1="12.6" x2="4.5" y2="11.5" />
        <line x1="11.5" y1="4.5" x2="12.6" y2="3.4" />
      </g>
    </svg>
  ),
  dark: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  ),
};

function ThemeToggle() {
  const { mode, cycle } = useTheme();
  const label = mode === 'system' ? 'System theme' : mode === 'light' ? 'Light theme' : 'Dark theme';
  return (
    <button
      type="button"
      onClick={cycle}
      title={`${label} — click to switch`}
      aria-label={`${label} (click to switch)`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 'var(--radius-sm)',
        border: '0.5px solid var(--color-border-strong)',
        background: 'var(--color-surface)', color: 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      {THEME_ICONS[mode]}
    </button>
  );
}

function NavItem({ to, label, icon, disabled, collapsed }: { to: string; label: string; icon: ReactNode; disabled?: boolean; collapsed?: boolean }) {
  const base: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: collapsed ? 0 : 8,
    padding: collapsed ? '6px 0' : '6px 12px',
    fontSize: 12, borderRadius: 6, margin: '1px 6px',
    color: 'var(--color-text-muted)', textDecoration: 'none',
    opacity: disabled ? 0.35 : 1, pointerEvents: disabled ? 'none' : undefined,
    whiteSpace: 'nowrap', overflow: 'hidden',
  };
  // When collapsed, the native title tooltip surfaces the label on hover —
  // without it the icon-only sidebar is unusable for anyone who hasn't
  // memorised every glyph.
  const tooltip = collapsed ? label : undefined;
  if (disabled) return <div style={base} title={tooltip}>{icon}{!collapsed && label}</div>;
  return (
    <NavLink
      to={to}
      title={tooltip}
      style={({ isActive }) => ({
        ...base,
        background: isActive ? 'var(--color-border)' : undefined,
        color: isActive ? 'var(--color-text)' : undefined,
        fontWeight: isActive ? 500 : undefined,
      })}
    >
      {icon}{!collapsed && label}
    </NavLink>
  );
}

const IconScorecard = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="9" width="4" height="6" rx="1" fill="currentColor"/><rect x="6" y="5" width="4" height="10" rx="1" fill="currentColor"/><rect x="11" y="1" width="4" height="14" rx="1" fill="currentColor"/></svg>;
const IconDeliverables = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2"/><line x1="4" y1="5" x2="12" y2="5" stroke="currentColor" strokeWidth="1.2"/><line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="1.2"/><line x1="4" y1="11" x2="9" y2="11" stroke="currentColor" strokeWidth="1.2"/></svg>;
const IconTeam = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="5" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.2"/><circle cx="11" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.2"/><path d="M1 13c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="currentColor" strokeWidth="1.2" fill="none"/><path d="M9 13c0-2.2 1.8-4 4-4" stroke="currentColor" strokeWidth="1.2" fill="none"/></svg>;
const IconCalendar = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><line x1="1" y1="7" x2="15" y2="7" stroke="currentColor" strokeWidth="1.2"/><line x1="5" y1="1" x2="5" y2="5" stroke="currentColor" strokeWidth="1.2"/><line x1="11" y1="1" x2="11" y2="5" stroke="currentColor" strokeWidth="1.2"/></svg>;
const IconUsers = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.2"/><path d="M2 13.5c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.2" fill="none"/></svg>;
const IconRefresh = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M13.5 8A5.5 5.5 0 1 1 8 2.5" stroke="currentColor" strokeWidth="1.2" fill="none"/><path d="M8 1v4l3-2-3-2z" fill="currentColor"/></svg>;
const IconOverview = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/><circle cx="8" cy="8" r="2.5" fill="currentColor"/></svg>;
const IconSignOut = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 14H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/><path d="M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>;

function SectionLabel({ children, collapsed }: { children: string; collapsed?: boolean }) {
  // Collapsed: render a thin separator instead of a text label — keeps the
  // section grouping visually intact without the text spilling out of the
  // narrow rail.
  if (collapsed) {
    return <div style={{ height: 1, margin: '8px 12px 4px', background: 'var(--color-border)' }} aria-hidden />;
  }
  return <div style={{ padding: '10px 16px 4px', fontSize: 10, fontWeight: 500, color: 'var(--color-text-hint)', letterSpacing: '.05em', textTransform: 'uppercase' }}>{children}</div>;
}

/** Shows the ended_at of the most recent successful/warning refresh run,
 *  formatted in the user's locale. Hidden while loading or if no runs exist. */
function LastRefreshPill() {
  const { data } = useQuery(() => api.admin.refreshRuns.list());
  const latest = (data?.runs ?? []).find((r) => r.status === 'success' || r.status === 'warning');
  if (!latest?.ended_at) return null;
  const d = new Date(latest.ended_at);
  const label = d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
  const color = latest.status === 'warning' ? 'amber' : 'green';
  const bg = color === 'amber' ? 'var(--color-amber-bg)' : 'var(--color-green-bg)';
  const fg = color === 'amber' ? 'var(--color-amber-text)' : 'var(--color-green-text)';
  return (
    <span
      title={`${latest.status === 'warning' ? 'Data sync completed with warnings' : 'Last successful data sync'} at ${d.toLocaleString()}`}
      style={{ background: bg, color: fg, fontSize: 11, padding: '2px 7px', borderRadius: 4, fontWeight: 500 }}
    >
      Last synced {label}
    </span>
  );
}

export default function Shell({ children }: { children: ReactNode }) {
  const { user, realUser, isImpersonating, stopImpersonation, switchUser, isSsoEnabled, signOut } = useAuth();
  const { collapsed, toggle: toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  // Nav gating mirrors App.tsx: everything non-FP&A derives from the effective
  // permission set resolved by /auth/me (definable in Admin → Permission
  // profiles; reports and admin surfaces are one key per page — is_admin
  // users hold every key); FP&A stays on the explicit fpa_role grant.
  const has = (key: string) => user?.permissions?.includes(key) === true;
  const isDM = user?.role === 'DM';
  const isSA = user?.role === 'SA';
  const isDirector = user?.role === 'DIRECTOR';
  const anyBilling = ['billing_imports', 'billing_receivables', 'billing_webhooks'].some(has);
  // Managers (DM/SA) get the team view. Directors get it too (their subtree).
  // This is people-management (org-tree) visibility, not a permission — the
  // /team page itself is open to every authenticated user.
  const isManager = isDM || isSA || isDirector;
  const anyReport = ['report_org_overview', 'report_company', 'report_forecast', 'report_deliverable_rollup',
    'report_org_chart', 'report_hygiene', 'report_at_risk', 'report_engagement_budget', 'report_invoicing',
    'report_commissions',
    'deal_onboarding', 'sales_overlay'].some(has);
  const canAccessUsers = has('admin_users') || has('impersonate');
  const anyAdmin = ['admin_users', 'admin_permission_profiles', 'admin_periods', 'admin_tiers',
    'admin_commission_rates', 'admin_commission_periods',
    'admin_clawbacks', 'admin_settings', 'admin_refresh', 'admin_audit'].some(has) || has('impersonate');
  // Sales dashboard — anyone "in sales" (linked HubSpot owner) + anyone
  // granted the sales permissions.
  const canAccessSales = user?.is_sales === true || has('sales_view') || has('sales_manage');
  const canAccessDashboard = has('exec_dashboard');
  // FP&A is gated on an explicit fpa_role grant — is_admin does NOT imply it.
  const canAccessFpa = !!user?.fpa_role;                                        // viewer+: Actuals
  const isFpaWriter = user?.fpa_role === 'analyst' || user?.fpa_role === 'approver'; // QBO mapping
  const isFpaApprover = user?.fpa_role === 'approver';                          // access management

  const pageTitle: Record<string, string> = {
    '/dashboard': 'Executive dashboard',
    '/sales': 'Sales performance',
    '/sales/quotas': 'Sales targets',
    '/scorecard': 'My scorecard',
    '/deliverables': 'My deliverables',
    '/team': 'Team view',
    '/admin/overview': 'Org overview',
    '/admin/users': 'Users & roles',
    '/admin/permission-profiles': 'Permission profiles',
    '/admin/periods': 'Period management',
    '/admin/reports/commissions': 'Sales commissions',
    '/admin/refresh': 'Data sync',
    '/admin/audit': 'Audit log',
    '/admin/tiers': 'Bonus tiers',
    '/admin/reports': 'Reports',
    '/admin/hygiene': 'Data hygiene',
    '/admin/at-risk': 'At-risk deliverables',
    '/admin/forecast': 'Forecast',
    '/admin/deliverable-rollup': 'Deliverable rollup',
    '/admin/cost-rates': 'Blended cost rate',
    '/admin/engagements': 'Engagements',
    '/admin/engagement-budget': 'Engagement budget',
    '/admin/invoicing': 'Invoicing',
    '/admin/sales-overlay': 'Sales overlay',
    '/admin/org-chart': 'Org chart',
    '/admin/clawbacks': 'Clawbacks',
    '/admin/billing':   'Billing',
    '/admin/billing/receivables': 'Receivables',
    '/admin/webhooks':  'Webhooks',
    '/admin/fpa/home': 'FP&A overview',
    '/admin/fpa/dashboard': 'FP&A dashboard',
    '/admin/fpa/plans': 'FP&A plans',
    '/admin/fpa/actuals':'FP&A actuals',
    '/admin/fpa/variance':'FP&A variance',
    '/admin/fpa/predictive':'Predictive model',
    '/admin/fpa/close': 'Period close',
    '/admin/fpa/qbo':   'QBO mapping',
    '/admin/fpa/access':'FP&A access',
    '/admin/settings':  'Settings',
    '/gantt':          'Timeline',
  };

  // Outer grid pins the sidebar (variable width based on collapsed state)
  // and main column to viewport height. The sidebar itself is a flex column
  // with three rows: pinned header (logo + collapse toggle), scrollable nav
  // (overflow-y: auto so a long sidebar doesn't push the footer off-screen
  // or clip), pinned footer (sign-out / dev switcher). Inside the main
  // column another grid stacks topbar / banner / scroll area with the
  // scroll area as the only flexible (1fr) row.
  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `${sidebarWidth}px 1fr`,
      width: '100%', height: '100vh',
      transition: 'grid-template-columns 0.15s ease',
    }}>
      {/* Sidebar */}
      <div style={{
        background: 'var(--color-surface)',
        borderRight: '0.5px solid var(--color-border)',
        display: 'flex', flexDirection: 'column', minHeight: 0,
        overflow: 'hidden',
      }}>
        {/* Header — pinned. Logo + collapse toggle. */}
        <div style={{
          padding: collapsed ? '12px 8px' : '14px 12px 12px 16px',
          borderBottom: '0.5px solid var(--color-border)',
          display: 'flex', alignItems: 'center', gap: 6,
          flexShrink: 0,
        }}>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <img
                src="/logo.png"
                alt="Unlimited"
                style={{ display: 'block', maxWidth: '100%', height: 'auto', maxHeight: 28 }}
              />
              <small style={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--color-text-muted)', marginTop: 6 }}>
                Operational Reporting
              </small>
            </div>
          )}
          <button
            type="button"
            onClick={toggleSidebar}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              border: '0.5px solid var(--color-border-strong)',
              background: 'var(--color-surface)', color: 'var(--color-text-muted)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              width: 24, height: 24, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 14, lineHeight: 1, padding: 0,
              flexShrink: 0,
            }}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        {/* Nav — the only scrollable strip. minHeight:0 + flex:1 lets it
            shrink inside the flex column so overflow-y kicks in properly. */}
        <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          <SectionLabel collapsed={collapsed}>Personal</SectionLabel>
          {canAccessDashboard && <NavItem collapsed={collapsed} to="/dashboard" label="Dashboard" icon={<IconOverview />} />}
          {canAccessSales && <NavItem collapsed={collapsed} to="/sales" label="Sales performance" icon={<IconScorecard />} />}
          <NavItem collapsed={collapsed} to="/scorecard" label="My scorecard" icon={<IconScorecard />} />
          <NavItem collapsed={collapsed} to="/deliverables" label="My deliverables" icon={<IconDeliverables />} />
          <NavItem collapsed={collapsed} to="/gantt" label="Timeline" icon={<IconCalendar />} />

          {isManager && (
            <>
              <SectionLabel collapsed={collapsed}>Manager</SectionLabel>
              <NavItem collapsed={collapsed} to="/team" label="Team view" icon={<IconTeam />} />
            </>
          )}

          {anyReport && (
            <>
              <SectionLabel collapsed={collapsed}>Reports</SectionLabel>
              {/* One permission per report — items render only for holders. */}
              {has('report_org_overview') && <NavItem collapsed={collapsed} to="/admin/overview" label="Org overview" icon={<IconOverview />} />}
              {has('report_company') && <NavItem collapsed={collapsed} to="/admin/reports" label="Reports" icon={<IconOverview />} />}
              {has('report_hygiene') && <NavItem collapsed={collapsed} to="/admin/hygiene" label="Data hygiene" icon={<IconDeliverables />} />}
              {has('report_at_risk') && <NavItem collapsed={collapsed} to="/admin/at-risk" label="At-risk" icon={<IconCalendar />} />}
              {has('report_forecast') && <NavItem collapsed={collapsed} to="/admin/forecast" label="Forecast" icon={<IconScorecard />} />}
              {has('report_deliverable_rollup') && <NavItem collapsed={collapsed} to="/admin/deliverable-rollup" label="Deliverable rollup" icon={<IconDeliverables />} />}
              {has('report_engagement_workspace') && <NavItem collapsed={collapsed} to="/admin/engagements" label="Engagements" icon={<IconOverview />} />}
              {has('report_engagement_budget') && <NavItem collapsed={collapsed} to="/admin/engagement-budget" label="Engagement budget" icon={<IconOverview />} />}
              {has('report_invoicing') && <NavItem collapsed={collapsed} to="/admin/invoicing" label="Invoicing" icon={<IconOverview />} />}
              {has('report_commissions') && <NavItem collapsed={collapsed} to="/admin/reports/commissions" label="Commissions" icon={<IconScorecard />} />}
              {has('deal_onboarding') && <NavItem collapsed={collapsed} to="/deal-onboarding" label="Deal onboarding" icon={<IconCalendar />} />}
              {/* Sales overlay blends HubSpot pipeline (closed-won + forecast) with
                  delivery (shipped + WIP). Deal-level data is sales-sensitive —
                  its key is granted deliberately, not part of any role default. */}
              {has('sales_overlay') && <NavItem collapsed={collapsed} to="/admin/sales-overlay" label="Sales overlay" icon={<IconScorecard />} />}
              {has('report_org_chart') && <NavItem collapsed={collapsed} to="/admin/org-chart" label="Org chart" icon={<IconTeam />} />}
            </>
          )}

          {anyBilling && (
            <>
              <SectionLabel collapsed={collapsed}>Billing</SectionLabel>
              {has('billing_imports') && <NavItem collapsed={collapsed} to="/admin/billing" label="ChargeOver billing" icon={<IconCalendar />} />}
              {has('billing_receivables') && <NavItem collapsed={collapsed} to="/admin/billing/receivables" label="Receivables" icon={<IconOverview />} />}
              {has('billing_webhooks') && <NavItem collapsed={collapsed} to="/admin/webhooks" label="ChargeOver webhooks" icon={<IconRefresh />} />}
            </>
          )}

          {canAccessFpa && (
            <>
              <SectionLabel collapsed={collapsed}>FP&amp;A</SectionLabel>
              <NavItem collapsed={collapsed} to="/admin/fpa/home" label="Overview" icon={<IconOverview />} />
              <NavItem collapsed={collapsed} to="/admin/fpa/dashboard" label="Dashboard" icon={<IconOverview />} />
              <NavItem collapsed={collapsed} to="/admin/fpa/plans" label="Plans" icon={<IconScorecard />} />
              <NavItem collapsed={collapsed} to="/admin/fpa/actuals" label="Actuals" icon={<IconOverview />} />
              <NavItem collapsed={collapsed} to="/admin/fpa/variance" label="Variance" icon={<IconScorecard />} />
              <NavItem collapsed={collapsed} to="/admin/fpa/predictive" label="Predictive model" icon={<IconScorecard />} />
              <NavItem collapsed={collapsed} to="/admin/fpa/close" label="Close" icon={<IconCalendar />} />
              {isFpaWriter && <NavItem collapsed={collapsed} to="/admin/fpa/qbo" label="QBO mapping" icon={<IconRefresh />} />}
              {isFpaApprover && <NavItem collapsed={collapsed} to="/admin/fpa/access" label="FP&A access" icon={<IconUsers />} />}
            </>
          )}

          {anyAdmin && (
            <>
              <SectionLabel collapsed={collapsed}>Admin</SectionLabel>
              {canAccessUsers && <NavItem collapsed={collapsed} to="/admin/users" label={has('admin_users') ? 'Users & roles' : 'Users (view as)'} icon={<IconUsers />} />}
              {has('admin_permission_profiles') && <NavItem collapsed={collapsed} to="/admin/permission-profiles" label="Permission profiles" icon={<IconUsers />} />}
              {(has('admin_periods') || has('admin_commission_periods')) && <NavItem collapsed={collapsed} to="/admin/periods" label="Period mgmt" icon={<IconCalendar />} />}
              {has('admin_tiers') && <NavItem collapsed={collapsed} to="/admin/tiers" label="Bonus tiers" icon={<IconScorecard />} />}
              {(has('admin_cost_rates') || has('report_profitability')) && <NavItem collapsed={collapsed} to="/admin/cost-rates" label="Blended cost rate" icon={<IconScorecard />} />}
              {has('admin_commission_rates') && <NavItem collapsed={collapsed} to="/admin/commissions" label="Sales commissions" icon={<IconScorecard />} />}
              {has('admin_clawbacks') && <NavItem collapsed={collapsed} to="/admin/clawbacks" label="Clawbacks" icon={<IconScorecard />} />}
              {has('admin_settings') && <NavItem collapsed={collapsed} to="/admin/settings" label="Settings" icon={<IconOverview />} />}
              {has('admin_refresh') && <NavItem collapsed={collapsed} to="/admin/refresh" label="Refresh history" icon={<IconRefresh />} />}
              {has('admin_audit') && <NavItem collapsed={collapsed} to="/admin/audit" label="Audit log" icon={<IconUsers />} />}
            </>
          )}
        </nav>

        {/* Footer — pinned. Dev persona switcher (off-SSO) / sign-out (SSO).
            When collapsed, the dev switcher hides (can't pick a user from
            a 56px-wide select); sign-out collapses to an icon-only button. */}
        <div style={{
          borderTop: '0.5px solid var(--color-border)',
          padding: collapsed ? 8 : 10, flexShrink: 0,
        }}>
          {isSsoEnabled ? (
            <button
              onClick={signOut}
              title={collapsed ? 'Sign out' : undefined}
              style={{
                width: '100%', fontSize: 11, padding: '6px 10px',
                borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border-strong)',
                background: 'var(--color-surface)', color: 'var(--color-text)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <IconSignOut />
              {!collapsed && 'Sign out'}
            </button>
          ) : !collapsed ? (
            <>
              <div style={{ fontSize: 10, color: 'var(--color-text-hint)', marginBottom: 4, paddingLeft: 4 }}>Dev: switch user</div>
              <select
                value={user?.jira_user_id}
                onChange={(e) => {
                  const u = DEV_USERS.find((u) => u.jira_user_id === e.target.value);
                  if (u) { switchUser(u); navigate('/scorecard'); }
                }}
                style={{ width: '100%', fontSize: 11, padding: '4px 6px', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border-strong)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
              >
                {[...DEV_USERS]
                  .sort((a, b) => a.display_name.localeCompare(b.display_name))
                  .map((u) => (
                    <option key={u.jira_user_id} value={u.jira_user_id}>{u.display_name} ({u.role})</option>
                  ))}
              </select>
            </>
          ) : (
            // Collapsed dev mode: tiny "D" badge as a visual reminder we're
            // running off the dev persona switcher. Clicking nothing — to
            // change persona, the user has to expand the sidebar.
            <div
              title="Dev persona — expand sidebar to switch"
              style={{
                width: '100%', fontSize: 10, padding: '4px 0',
                color: 'var(--color-text-muted)', textAlign: 'center',
                border: '0.5px dashed var(--color-border-strong)',
                borderRadius: 'var(--radius-sm)',
              }}
            >DEV</div>
          )}
        </div>
      </div>

      {/* Main column. Grid rows: topbar (auto), optional banner (auto),
          page content (1fr — scrollable). minHeight:0 lets the column
          actually consume only the viewport height instead of growing to
          fit children. */}
      <div style={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', minHeight: 0, height: '100vh' }}>
        {/* Topbar */}
        <div style={{ background: 'var(--color-surface)', borderBottom: '0.5px solid var(--color-border)', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{pageTitle[location.pathname] ?? 'ORP'}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LastRefreshPill />
            <ThemeToggle />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar initials={initials(user?.display_name ?? 'U')} size={26} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{user?.display_name}</div>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{user?.title || user?.role} · {user?.team}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Banner is always rendered (display:none when inactive) so the
            grid's 3-row template (topbar / banner / content) matches the
            DOM regardless of impersonation state. */}
        <div
          style={{
            display: isImpersonating ? 'flex' : 'none',
            background: '#fef3c7', color: '#78350f', borderBottom: '0.5px solid #fbbf24',
            padding: '8px 20px', alignItems: 'center', justifyContent: 'space-between', fontSize: 12,
          }}
        >
          <span>
            Viewing as <strong>{user?.display_name}</strong> ({user?.role}) — signed in as {realUser?.display_name}. All actions in this session are recorded in the audit log.
          </span>
          <button
            onClick={async () => { await stopImpersonation(); navigate('/admin/users'); }}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, border: '0.5px solid #b45309', background: '#fde68a', color: '#78350f', cursor: 'pointer', fontWeight: 500 }}
          >
            Stop impersonating
          </button>
        </div>

        {/* Page content. The OUTER div is the scroll viewport (bounded by
            the 1fr grid row). The INNER div lays children out as a flex
            column with gap. We must NOT make the outer div a flex container
            — under flex pressure children would shrink (flex-shrink: 1) and
            the Card component's overflow:hidden silently clips them instead
            of triggering a scrollbar. */}
        <div style={{ minHeight: 0, overflowY: 'auto' }}>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
